import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SessionStorageService } from '../session-storage.service';
import { Plan, PlanDetails, PLAN_CATALOG } from '../models/planDetails';
import { LocalStorageKey } from '../models/storageKey';
import { AnalyticsService } from '../analytics/analytics.service';
import { FunnelTrackingService } from '../analytics/funnel-tracking.service';

@Component({
  selector: 'app-choose-service-plan',
  templateUrl: './choose-service-plan.component.html',
  styleUrls: ['./choose-service-plan.component.css']
})
export class ChooseServicePlanComponent implements OnInit, OnDestroy {
  selectedPlanName = '';
  isAnnual = true;
  venueName = '';
  recommendedPlanName: Plan = 'essential';

  private planTouched = false;
  private pageStartTime = 0;
  private billingFrequencyChangeCount = 0;
  private storageSubscription?: Subscription;

  plans: PlanDetails[] = PLAN_CATALOG;

  constructor(
    private session: SessionStorageService,
    private router: Router,
    private analytics: AnalyticsService,
    private funnelTracking: FunnelTrackingService
  ) {}

  ngOnInit(): void {
    // Step views and page timing are handled by FunnelTrackingService;
    // pageStartTime only feeds intra-page interaction timings below.
    this.pageStartTime = Date.now();

    // Restore reactively until the user interacts; the session doc restore
    // is async. The recommendation is recomputed from the venue answers and
    // preselected when no plan was chosen yet.
    this.storageSubscription = this.session.storage$.subscribe(storage => {
      this.venueName = storage[LocalStorageKey.VENUE_NAME] || this.venueName;
      this.recommendedPlanName = this.recommendPlan(storage);
      if (this.planTouched) {
        return;
      }
      this.selectedPlanName = storage[LocalStorageKey.SELECTED_PLAN] || this.recommendedPlanName;
      if (storage[LocalStorageKey.BILLING_FREQUENCY]) {
        this.isAnnual = storage[LocalStorageKey.BILLING_FREQUENCY] === 'annual';
      }
    });

    // Track individual plan views (for comparison analysis)
    this.plans.forEach(plan => {
      this.analytics.trackEvent('plan_viewed', {
        plan_name: plan.name,
        plan_price_monthly: parseFloat(plan.monthlyPrice),
        plan_price_annual: parseFloat(plan.annualPrice),
        billing_frequency: this.isAnnual ? 'annual' : 'monthly',
        displayed_price: this.isAnnual ? parseFloat(plan.annualMonthlyPrice) : parseFloat(plan.monthlyPrice)
      });
    });
  }

  ngOnDestroy(): void {
    this.storageSubscription?.unsubscribe();
  }

  get recommendedPlan(): PlanDetails {
    return this.plans.find(plan => plan.name === this.recommendedPlanName) || this.plans[2];
  }

  get headline(): string {
    return `${this.recommendedPlan.displayName} is the right fit for ${this.venueName || 'your venue'}.`;
  }

  get selectedPlan(): PlanDetails | null {
    return this.plans.find(plan => plan.name === this.selectedPlanName) || null;
  }

  displayedPrice(plan: PlanDetails): string {
    return this.isAnnual ? plan.annualMonthlyPrice : plan.monthlyPrice;
  }

  setPricing(isAnnual: boolean): void {
    const previousFrequency = this.isAnnual;
    this.isAnnual = isAnnual;
    this.planTouched = true;

    if (previousFrequency !== isAnnual) {
      this.billingFrequencyChangeCount++;

      // Track billing frequency change
      this.analytics.trackEvent('billing_frequency_changed', {
        from_frequency: previousFrequency ? 'annual' : 'monthly',
        to_frequency: isAnnual ? 'annual' : 'monthly',
        change_count: this.billingFrequencyChangeCount,
        time_since_page_load: Date.now() - this.pageStartTime
      });
    }
  }

  onSelectPlan(plan: PlanDetails): void {
    this.planTouched = true;
    this.selectedPlanName = plan.name;
  }

  onBack(): void {
    this.router.navigate(['/venueType']);
  }

  onContinue(): void {
    const plan = this.selectedPlan;
    if (!plan) {
      this.analytics.trackFormError('plan_selection', 'no_plan_selected', 'plan_selection',
        'No plan selected');
      alert('Please select a plan before proceeding.');
      return;
    }

    const billingFrequency = this.isAnnual ? 'annual' : 'monthly';
    const planPrice = parseFloat(this.displayedPrice(plan));
    const totalPrice = this.isAnnual ? parseFloat(plan.annualPrice) : planPrice;

    // plan_selected fires here, at the step's single success point, so
    // keeping the preselected recommendation counts the same as picking a
    // card — and switching between cards can't multi-fire it.
    this.analytics.trackPlanSelection(plan.name, planPrice, billingFrequency);

    // Track signup step completion
    this.funnelTracking.trackStepCompleted('plan_selection', {
      plan_name: plan.name,
      billing_frequency: billingFrequency,
      plan_price: planPrice,
      total_annual_value: this.isAnnual ? totalPrice : planPrice * 12,
      was_recommended: plan.name === this.recommendedPlanName
    });

    // Set user properties
    this.analytics.setUserProperties({
      selected_plan: plan.name,
      billing_frequency: billingFrequency,
      plan_price: planPrice,
      signup_step: 'plan_selected'
    });

    // Save to session storage
    this.session.setItem(LocalStorageKey.SELECTED_PLAN, plan.name);
    this.session.setItem(LocalStorageKey.BILLING_FREQUENCY, billingFrequency);
    this.session.setItem(LocalStorageKey.PLAN, 'complete');

    this.router.navigate(['review']);
  }

  /**
   * Recommend a plan from the venue-step answers. Mirrors the plan
   * positioning: nonprofit for 501(c)(3) school/booster stands, enterprise
   * for stadium-scale operations, starter for small occasional volume,
   * essential otherwise.
   */
  private recommendPlan(storage: Record<string, unknown>): Plan {
    const nonprofit = storage[LocalStorageKey.NONPROFIT_STATUS] || '';
    const category = storage[LocalStorageKey.VENUE_CATEGORY_TYPE] || '';
    const venueType = storage[LocalStorageKey.VENUE_TYPE] || '';
    const orders = storage[LocalStorageKey.MONTHLY_ORDER_VOLUME] || '';

    const isSchoolStand = category === 'Concession Stand' && venueType !== 'Independent/Community';
    if (nonprofit === 'yes' || (isSchoolStand && nonprofit !== 'no')) {
      return 'nonprofit';
    }
    if (category === 'Stadium/Arena' || category === 'Sports Complex' || orders === 'Over 10,000') {
      return 'enterprise';
    }
    if (orders === 'Under 500') {
      return 'starter';
    }
    return 'essential';
  }
}
